/**
 * useWizardDraft — localStorage snapshot helper for offline-first
 * multi-step wizards (Commissioning, PM Service Visit, Warranty Claim).
 *
 * iter150AZ.69 — Phase 5 of the offline-first work. The earlier phases
 * (.66/.67/.68) made each wizard's network mutations idempotent and
 * SW-queueable. This phase makes the **local React state** survive a
 * tab close so a tech who shuts the lid mid-wizard can pick up where
 * they left off — even completely offline.
 *
 * API:
 *   loadDraft(kind)        — returns the snapshot or null. Auto-expires
 *                            after EXPIRY_MS.
 *   saveDraft(kind, state) — writes the snapshot. Sanitises non-
 *                            serialisable bits (Blob / File / ObjectURL).
 *   clearDraft(kind)       — removes the snapshot. Call on submit success.
 *   useWizardDraft(kind, state, deps)
 *                          — React hook: throttles saveDraft to once per
 *                            ~500 ms while any dep changes. No saves
 *                            for falsy snapshots (so the very first
 *                            render with nothing chosen yet doesn't
 *                            litter localStorage).
 *
 * Each wizard chooses what to persist. We keep it minimal — IDs +
 * a step pointer + small fields the user has typed. We never store
 * raw File / Blob bytes (those live in the SW outbox under IDB).
 *
 * The "kind" string is the wizard identifier; the storage key is
 * ``usair_wizard_draft_<kind>`` (prefix isolated so we can wipe all
 * drafts in a future "Reset offline state" admin tool).
 */
import { useEffect, useRef } from "react";

const PREFIX    = "usair_wizard_draft_";
const EXPIRY_MS = 7 * 24 * 60 * 60 * 1000;   // 7 days — long enough for a long offline trip

function _isPersistable(v) {
  if (v == null) return true;
  if (typeof v === "function") return false;
  if (typeof Blob !== "undefined" && v instanceof Blob) return false;
  if (typeof File !== "undefined" && v instanceof File) return false;
  return true;
}

function _sanitise(v) {
  if (!_isPersistable(v)) return undefined;
  if (Array.isArray(v)) return v.map(_sanitise).filter((x) => x !== undefined);
  if (v && typeof v === "object") {
    const out = {};
    for (const k of Object.keys(v)) {
      // Common non-serialisable shapes we encounter in these wizards:
      // - `file`, `previewUrl`, `blob` properties on capture entries
      // - ObjectURL strings (blob:…) — runtime URLs, useless after reload
      if (k === "file" || k === "blob" || k === "previewUrl") continue;
      if (typeof v[k] === "string" && v[k].startsWith("blob:")) continue;
      const s = _sanitise(v[k]);
      if (s !== undefined) out[k] = s;
    }
    return out;
  }
  return v;
}

export function loadDraft(kind) {
  try {
    const raw = localStorage.getItem(PREFIX + kind);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (data?._savedAt && (Date.now() - data._savedAt > EXPIRY_MS)) {
      // Auto-purge stale drafts so techs don't see month-old work
      // pop back up when they reopen the wizard fresh.
      localStorage.removeItem(PREFIX + kind);
      return null;
    }
    return data;
  } catch (_) {
    return null;
  }
}

export function saveDraft(kind, state) {
  try {
    const clean = _sanitise(state);
    if (!clean) return;
    localStorage.setItem(PREFIX + kind, JSON.stringify({
      ...clean,
      _savedAt: Date.now(),
    }));
  } catch (_) {
    // Quota exceeded etc — silent. The offline outbox is the source
    // of truth for in-flight network work; localStorage is just a
    // resume convenience.
  }
}

export function clearDraft(kind) {
  try { localStorage.removeItem(PREFIX + kind); } catch (_) { /* ignore */ }
}

export function useWizardDraft(kind, state, deps) {
  const timer = useRef(null);
  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      // Skip empty-state writes so the very-first render doesn't
      // clobber an existing draft with `{step: "pick", ...}`.
      const hasMeaningful = state && (
        state.visit || state.claim || state.selected || state.step
        || state.preflight || state.captures || state.findings
      );
      if (hasMeaningful) saveDraft(kind, state);
    }, 500);
    return () => { if (timer.current) clearTimeout(timer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
